/**
 * EO «Конфигуратор → Спецверсии» — панель суб-таба по эталону renderSpecs
 * (screen-17-quests.js:480-496; A35). BranchTabs → шапка «Спецверсии:
 * {филиал} (N)» + Добавить/Отмена (abtn cf solo) → форма создания (.cform)
 * → аккордеон карточек: collapsed — имя + бейдж базового short (warn «не
 * привязана» без базы) + мета Игра/Пауза/Δ/мини-операторы; expanded —
 * SpecCardBody. Удаление — одностадийный CfgModal (js:476-479). Слотов у
 * версии нет (сетка — базового квеста), dirty-guard не нужен. Жесты A43:
 * свайп-вправо по collapsed-карточке — форма создания с данными исходника
 * (клон рождается непривязанным, в БД — по «Создать»); pull-to-refresh.
 */
import { useEffect, useState } from "react";
import type { QuestVersion } from "../../api/types";
import { BranchTabs } from "../../components/grid";
import { PullHud } from "../../components/shared";
import { useAppData } from "../../hooks/useAppData";
import { useAppReload, usePullToRefresh } from "../../hooks/usePullToRefresh";
import { useSpecsAdmin } from "../../hooks/useSpecsAdmin";
import { CfgModal } from "./EoConfigModal";
import { crewToOps, OpsMini, opsToCrew } from "./OpsRow";
import { fmtD } from "./QuestFormCells";
import { CrossIco, PlusIco } from "./questIcons";
import {
  initSpecDraft, SpecCardBody, SpecCreateForm,
  type SpecCreateDraft, type SpecFormDraft,
} from "./SpecCardForm";
import { SwipeDup } from "./SwipeDup";
import cfg from "./config.module.css";
import css from "./quests.module.css";

const C0: SpecCreateDraft = { name: "", dur: 90, reset: 15, ops: ["a"] };

export function EoConfigSpecs() {
  const app = useAppData();
  const [branchId, setBranchId] = useState("");
  const sa = useSpecsAdmin(branchId);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [draft, setDraft] = useState<SpecFormDraft | null>(null);
  const [secOpen, setSecOpen] = useState<Record<string, boolean>>({});
  const [cOpen, setCOpen] = useState(false);
  const [cBusy, setCBusy] = useState(false);
  const [cForm, setCForm] = useState<SpecCreateDraft>(C0);
  const [del, setDel] = useState<QuestVersion | null>(null);
  /* Исходник свайп-дубликата: форма создания предзаполнена им (A43.1). */
  const [cloneSrc, setCloneSrc] = useState<QuestVersion | null>(null);
  const reloadApp = useAppReload();
  const ptr = usePullToRefresh(() => { sa.refetch(); return reloadApp(); });

  /* Дефолт — первый активный филиал (паттерн EoConfigQuests). */
  useEffect(() => {
    if (!branchId) {
      const first = app.branches.find((x) => x.active);
      if (first) setBranchId(first.id);
    }
  }, [app.branches, branchId]);

  const branch = app.branches.find((b) => b.id === branchId) ?? null;

  const pickBranch = (id: string) => {
    if (id === branchId) return;
    setBranchId(id); setExpanded(null); setDraft(null); setCOpen(false); setCloneSrc(null);
  };

  const toggleCard = (v: QuestVersion) => {
    if (expanded === v.id) { setExpanded(null); setDraft(null); }
    else { setExpanded(v.id); setDraft(initSpecDraft(v)); }
  };

  const toggleCreate = () => { setCOpen((o) => !o); setCBusy(false); setCForm(C0); setCloneSrc(null); };

  /* A43.1: свайп-вправо — форма создания с данными исходника; копируется
     всё, кроме привязки base и видимости — клон рождается непривязанным
     и скрытым (A35: новая — скрыта, пока не готова). */
  const dupSpec = (v: QuestVersion) => {
    sa.loadVersionCons(v.id); // прогрев кеша — фолбэк versionConsFor при ошибке сети
    setCloneSrc(v);
    setCForm({
      /* slice: sanitize title бросает 400 при >200 симв. */
      name: `${v.title} (копия)`.slice(0, 200),
      dur: v.duration_min ?? v.base_duration ?? 90,
      reset: v.reset_min ?? v.base_reset ?? 15,
      ops: crewToOps(v.operators_comp),
    });
    setCBusy(false);
    setCOpen(true);
  };

  const submitCreate = () => {
    if (cBusy || !cForm.name.trim()) return;
    const name = cForm.name.trim();
    const src = cloneSrc;
    setCBusy(true);
    /* Нормы исходника — явный GET параллельно POST, кеш — фолбэк сети
       (race: ленивый прогрев dupSpec мог не успеть — клон терял нормы). */
    const consP = src ? sa.versionConsFor(src.id) : null;
    void sa.createSpec({ title: name, dur: cForm.dur, reset: cForm.reset, crew: opsToCrew(cForm.ops) })
      .then(async (id) => {
        if (!id) { setCBusy(false); return; }
        const cc = consP ? await consP : null;
        setCBusy(false); setCOpen(false); setCForm(C0); setCloneSrc(null);
        /* Клон: цены — в БД по «Создать» (saveSpec вдогонку POST); видимость
           НЕ наследуется (принцип A35 — тумблер включит владелец после
           привязки); нормы без базы в БД не живут (скоуп quest_id) — идут
           в драфт и сохранятся «Сохранить» после привязки. */
        const d: SpecFormDraft = {
          id, name, base: null, dur: cForm.dur, reset: cForm.reset, ops: [...cForm.ops],
          cons: cc?.length ? cc : null,
          pn: src?.base_players ?? 4, pb: src?.base_price ?? 0, pp: src?.price_per_extra ?? 0,
          pub: false,
        };
        if (src) {
          const nv: QuestVersion = {
            id, title: name, slot_quest_id: null, duration_min: cForm.dur, reset_min: cForm.reset,
            operators_comp: opsToCrew(cForm.ops) || null, client_visible: 0,
            base_players: d.pn, base_price: d.pb, price_per_extra: d.pp,
          };
          void sa.saveSpec(nv, {
            title: name, slot_quest_id: null, duration_min: cForm.dur, reset_min: cForm.reset,
            crew: opsToCrew(cForm.ops), client_visible: false, cons: null,
            pn: d.pn, pb: d.pb, pp: d.pp,
          });
        }
        setExpanded(id);
        setDraft(d);
      });
  };

  if (sa.loading) {
    return (
      <div className={cfg.scroll}>
        <div className={cfg.skel}>
          <div className={`${cfg.sk} ${cfg.skTitle}`} />
          {[0, 1, 2, 3].map((i) => <div key={i} className={`${cfg.sk} ${cfg.skTea}`} />)}
        </div>
      </div>
    );
  }

  return (
    <>
      <BranchTabs
        branches={app.branches.filter((x) => x.active)}
        active={branchId}
        onChange={pickBranch}
        allChip={false}
        accent="role"
      />
      <div className={cfg.scroll} ref={ptr.ref}>
        <PullHud pull={ptr.pull} pulling={ptr.pulling} refreshing={ptr.refreshing} />
        <div className={css.s17head}>
          <h2 className={cfg.secT}>Спецверсии: {branch?.name ?? "—"} ({sa.versions.length})</h2>
          <button type="button" className={`${css.abtn} ${css.cf} ${css.solo}`} onClick={toggleCreate}>
            {cOpen ? <><CrossIco w={11} />Отмена</> : <><PlusIco />Добавить</>}
          </button>
        </div>
        {sa.hint ? <div className={cfg.errHint} style={{ margin: "0 8px" }}>{sa.hint}</div> : null}
        {cOpen ? (
          <SpecCreateForm f={cForm} setF={setCForm} busy={cBusy} onCreate={submitCreate} onCancel={toggleCreate} />
        ) : null}
        {sa.versions.map((v) => {
          const open = expanded === v.id;
          const bq = v.slot_quest_id ? sa.quests.find((x) => x.id === v.slot_quest_id) : undefined;
          const baseDur = bq?.duration ?? v.base_duration ?? null;
          const dur = v.duration_min ?? baseDur ?? 0;
          const f = sa.flash?.key === `card:${v.id}` ? sa.flash : null;
          return (
            <article
              key={f ? `${v.id}:${f.n}` : v.id}
              className={`${css.qcard} ${open ? css.open : ""} ${f ? cfg.flash : ""}`}
            >
              <SwipeDup disabled={open} onDup={() => dupSpec(v)} className={css.qrow} onClick={() => toggleCard(v)}>
                <div className={css.qtop}>
                  <span className={css.qname}><span className={css.mq}>{v.title}</span></span>
                  {v.slot_quest_id ? (
                    <span className={css.qshort}>{bq?.short_name ?? v.base_short ?? v.slot_quest_id}</span>
                  ) : (
                    <span className={`${css.qshort} ${css.warn}`}>не привязана</span>
                  )}
                  <button
                    type="button"
                    className={`${css.tx} ${css.qdel}`}
                    title="Удалить"
                    onClick={(e) => { e.stopPropagation(); setDel(v); }}
                  >
                    <CrossIco />
                  </button>
                </div>
                <div className={css.qmeta}>
                  <span className={`${css.m} ${css.mc}`}>Игра: <span className={css.num}>{dur}м</span></span>
                  <span className={`${css.m} ${css.mo}`}>Пауза: <span className={css.num}>{v.reset_min ?? v.base_reset ?? 0}м</span></span>
                  {v.slot_quest_id && baseDur != null ? (
                    <span className={`${css.m} ${css.mc}`}><span className={css.num}>{fmtD(dur - baseDur)}м</span></span>
                  ) : null}
                  <span className={css.m}><OpsMini ops={crewToOps(v.operators_comp)} /></span>
                </div>
              </SwipeDup>
              {open && draft?.id === v.id ? (
                <SpecCardBody
                  v={v} sa={sa} draft={draft} setDraft={setDraft}
                  onCancel={() => setDraft(initSpecDraft(v))}
                  secOpen={secOpen} onSec={(k) => setSecOpen((s) => ({ ...s, [k]: !s[k] }))}
                />
              ) : null}
            </article>
          );
        })}
        {/* Пустышка прячется и при открытой форме создания (js:482). */}
        {!sa.versions.length && !cOpen ? <div className={cfg.teaEmpty}>Нет спецверсий — добавьте первую.</div> : null}
      </div>
      {del ? (
        <CfgModal
          tone="danger"
          title="Удалить спецверсию?"
          actionLabel="Удалить"
          onAction={() => {
            sa.deleteSpec(del);
            if (expanded === del.id) { setExpanded(null); setDraft(null); }
            setDel(null);
          }}
          onCancel={() => setDel(null)}
        >
          «<b>{del.title}</b>» будет удалена.
          <span className={cfg.mSub}>Базовый квест и его сетка не меняются.</span>
        </CfgModal>
      ) : null}
    </>
  );
}
